import { useCallback } from 'react';
import { useUrlFilters } from '@/shared/hooks';
import type { ReportsParams } from '../types';

const REPORT_TYPES = ['manual', 'auto', 'scheduled'] as const;
type ReportType = (typeof REPORT_TYPES)[number];

const REPORT_STATUSES = ['generating', 'completed', 'failed', 'pending', 'processing'] as const;
type ReportStatus = (typeof REPORT_STATUSES)[number];

/**
 * Хук для работы с фильтрами отчетов
 */
export const useReportsFilters = () => {
  const validateValue = useCallback((filterKey: string, value: string) => {
    if (filterKey === 'type') {
      return REPORT_TYPES.includes(value as ReportType);
    }
    if (filterKey === 'status') {
      return REPORT_STATUSES.includes(value as ReportStatus);
    }
    return true;
  }, []);

  const {
    filters,
    setFilters,
    handleApplyFilters,
    handleCancelFilters,
    handleRemoveFilter,
    getActiveFilters,
  } = useUrlFilters({
    validateValue,
    excludeFromActiveFilters: [], // Тип тоже может быть в активных фильтрах
  });

  const toQueryParams = useCallback((): ReportsParams | undefined => {
    const params: ReportsParams = {};

    const typeFilters = filters['type'] || [];
    if (typeFilters.length > 0) {
      // Если выбран только один тип, передаем его
      if (typeFilters.length === 1) {
        const type = typeFilters[0];
        if (REPORT_TYPES.includes(type as ReportType)) {
          // Маппим 'scheduled' в 'auto' для бэкенда, если он не поддерживает scheduled отдельно
          params.type = type === 'scheduled' ? 'auto' : (type as 'manual' | 'auto');
        }
      }
      // Если выбрано несколько типов, можно передать первый или обработать иначе
      // Пока передаем первый выбранный тип
    }

    const statusFilters = filters['status'] || [];
    if (statusFilters.length === 1) {
      const status = statusFilters[0];
      if (REPORT_STATUSES.includes(status as ReportStatus)) {
        params.status = status as ReportStatus;
      }
    }

    const createdDateFilters = filters['created_at'] || [];
    if (createdDateFilters.length === 1) {
      params.created_at = createdDateFilters[0];
    }

    return Object.keys(params).length > 0 ? params : undefined;
  }, [filters]);

  return {
    filters,
    setFilters,
    handleApplyFilters,
    handleCancelFilters,
    handleRemoveFilter,
    getActiveFilters,
    toQueryParams,
  };
};
